import type { ComponentType, ReactNode } from 'react';
import { PassthroughErrorBoundary } from './PassthroughErrorBoundary';

/**
 * Minimal user context passed to the error reporter after authentication
 * resolves. Matches the common shape supported by Sentry, Bugsnag, and
 * similar providers.
 *
 * The opaque `id` is the only field, deliberately — mirrors
 * `@reflex/plugin-utils/error-reporter` on the server. Scope user data rides
 * along on every envelope the SDK emits (errors, transactions, replays), and
 * only the error envelope passes through a `beforeSend` scrub, so the
 * identifying fields are kept off the scope rather than scrubbed after the
 * fact. Join `id` against the users table for the rest.
 */
export interface ErrorReporterUser {
  id: string;
}

/**
 * Props accepted by the provider-agnostic error boundary component.
 * Mirrors the subset of `Sentry.ErrorBoundary` props we actually use.
 */
export interface ErrorBoundaryProps {
  fallback: ReactNode;
  children?: ReactNode;
}

/**
 * Generic error reporting contract for the web app. Implementations wrap a
 * concrete provider (Sentry, Datadog, console-only, etc.) so that UI code
 * stays decoupled from any specific SDK.
 */
export interface WebErrorReporter {
  captureException(error: unknown): void;
  setUser(user: ErrorReporterUser | null): void;
  /**
   * React error boundary. Falls through to a passthrough fragment when no
   * provider is registered, matching the previous behaviour for local
   * development (where Sentry is disabled).
   */
  ErrorBoundary: ComponentType<ErrorBoundaryProps>;
}

/** No-op reporter used when no provider has been registered. */
export const noopWebErrorReporter: WebErrorReporter = {
  captureException: () => {},
  setUser: () => {},
  ErrorBoundary: PassthroughErrorBoundary,
};

let activeReporter: WebErrorReporter = noopWebErrorReporter;

/** Register the active error reporter. Call once at startup. */
export function initErrorReporter(reporter: WebErrorReporter): void {
  activeReporter = reporter;
}

/** Resolve the active error reporter. Defaults to a no-op implementation. */
export function getErrorReporter(): WebErrorReporter {
  return activeReporter;
}

/** Reset to the no-op reporter. Intended for tests. */
export function resetErrorReporter(): void {
  activeReporter = noopWebErrorReporter;
}
